import React, { useEffect, useState, useRef } from "react";
import PropTypes from 'prop-types';
import api from "../../api/api";
import { useTranslation } from "react-i18next";

const UploadBiodata = ({ extractedBiodata }) => {
  const { t } = useTranslation();
  const [error, setError] = useState();
  const [blockUploading, setBlockUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [taskId, setTaskId] = useState(null);
  const [resultPDF, setResultPDF] = useState(null);

  const handlePDFChange = async (e) => {
    setResultPDF(null);
    const file = e.target.files[0];

    if (file) {
      const fileName = file.name;
      const extension = fileName.split('.').pop().toLowerCase();
      const allowedExtensions = ['pdf'];

      if (!allowedExtensions.includes(extension)) {
        setError(t('Unsupported file extension (only pdf allowed)'));
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        setError(t('File size exceeds the maximum limit of 2MB'));
        return;
      }

      if (blockUploading) return;
      
      setBlockUploading(true);
      setResultPDF("uploading");
      setError("");

      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post('/biodata/upload/', formData);
        setTaskId(res.data.task_id);
        setResultPDF("processing");
      } catch (error) {
        setError(t('An error occurred while processing the file'));
        setResultPDF(null);
      } finally {
        setBlockUploading(false);
      }
    }
  };

  useEffect(() => {
    if (!taskId) return;

    const interval = setInterval(async () => {
      const res = await api.get(`/task-status/${taskId}/`);
      
      if (res.data.status === 'SUCCESS') {
        extractedBiodata(JSON.parse(res.data.result));
        setResultPDF("success");
        clearInterval(interval);
      } else if (res.data.status === 'FAILURE') {
        setError(t('An error occurred while processing the PDF'));
        setResultPDF(null);
        clearInterval(interval);
      } else if (res.data.status === 'PENDING') {
        setResultPDF("processing");
      } else {
        setResultPDF("processing");
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [taskId]);

  const renderStatus = () => {
    if (!resultPDF) return null;

    const statusConfig = {
      uploading: {
        icon: 'fas fa-cloud-upload-alt',
        color: 'primary',
        bgColor: 'rgba(13, 110, 253, 0.1)',
        borderColor: '#0d6efd',
        message: t('Uploading PDF...')
      },
      processing: {
        icon: 'fas fa-cog fa-spin',
        color: 'warning',
        bgColor: 'rgba(255, 193, 7, 0.1)',
        borderColor: '#ffc107',
        message: t('Processing PDF, please wait...')
      },
      success: {
        icon: 'fas fa-check-circle',
        color: 'success',
        bgColor: 'rgba(25, 135, 84, 0.1)',
        borderColor: '#198754',
        message: t('PDF processed successfully!')
      }
    };

    const config = statusConfig[resultPDF];
    if (!config) return null;

    return (
      <div 
        className="mt-3 p-3 rounded-3 border-start border-3"
        style={{ 
          backgroundColor: config.bgColor,
          borderLeftColor: config.borderColor + ' !important',
          transition: 'all 0.3s ease'
        }}
      >
        <div className="d-flex align-items-center">
          <div 
            className="rounded-circle d-flex align-items-center justify-content-center me-3"
            style={{ 
              width: '40px', 
              height: '40px', 
              backgroundColor: config.borderColor + '20' 
            }}
          >
            <i className={`${config.icon} text-${config.color}`}></i>
          </div>
          <div className="flex-grow-1">
            <p className={`text-${config.color} fw-semibold mb-0`}>
              {config.message}
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Custom styles object
  const uploadZoneStyles = {
    transition: 'all 0.3s ease',
    cursor: 'pointer',
  };

  const uploadZoneHoverStyles = {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
  };

  return (
    <div className="modern-upload-container" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {/* Modern Upload Zone */}
      <div 
        className="upload-zone position-relative p-4 rounded-4 border-2 border-dashed text-center"
        style={{
          ...uploadZoneStyles,
          borderColor: error ? '#dc3545' : (resultPDF ? '#198754' : '#dee2e6'),
          backgroundColor: error ? 'rgba(220, 53, 69, 0.05)' : (resultPDF ? 'rgba(25, 135, 84, 0.05)' : 'rgba(248, 249, 250, 0.8)'),
        }}
        onClick={() => fileInputRef.current?.click()}
        onMouseEnter={(e) => {
          Object.assign(e.currentTarget.style, uploadZoneHoverStyles);
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'none';
          e.currentTarget.style.boxShadow = 'none';
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.currentTarget.style.borderColor = '#0d6efd';
          e.currentTarget.style.backgroundColor = 'rgba(13, 110, 253, 0.1)';
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.currentTarget.style.borderColor = '#dee2e6';
          e.currentTarget.style.backgroundColor = 'rgba(248, 249, 250, 0.8)';
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.currentTarget.style.borderColor = '#dee2e6';
          e.currentTarget.style.backgroundColor = 'rgba(248, 249, 250, 0.8)';
          
          const files = e.dataTransfer.files;
          if (files.length > 0) {
            fileInputRef.current.files = files;
            handlePDFChange({ target: { files } });
          }
        }}
      >
        {/* Hidden File Input */}
        <input
          type="file"
          id="pdfprofile"
          name="pdfprofile"
          accept="application/pdf"
          className="d-none"
          onChange={handlePDFChange}
          ref={fileInputRef}
        />

        {/* Upload Icon & Content */}
        <div className="upload-content">
          <div 
            className="upload-icon mx-auto mb-3 rounded-circle d-flex align-items-center justify-content-center"
            style={{ 
              width: '80px', 
              height: '80px', 
              backgroundColor: error ? 'rgba(220, 53, 69, 0.1)' : 'rgba(13, 110, 253, 0.1)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <i 
              className={`fas ${error ? 'fa-exclamation-triangle text-danger' : 'fa-file-pdf text-primary'}`}
              style={{ fontSize: '2rem' }}
            ></i>
          </div>

          <h6 className="fw-bold mb-2" style={{ color: '#495057' }}>
            <i className="fas fa-cloud-upload-alt me-2 text-primary"></i>
            {t("Upload PDF Document")}
          </h6>

          <p className="text-muted mb-2 small">
            {t("Drag and drop your PDF file here, or")} 
            <span className="text-primary fw-semibold ms-1">{t("click to browse")}</span>
          </p>

          <div className="d-flex justify-content-center gap-3 text-muted small">
            <span>
              <i className="fas fa-file-pdf me-1"></i>
              {t("PDF only")}
            </span>
            <span>
              <i className="fas fa-weight-hanging me-1"></i>
              {t("Max 2MB")}
            </span>
          </div>
        </div>

        {/* Reset Button */}
        {(resultPDF || error) && (
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm position-absolute"
            style={{ top: '15px', right: '15px' }}
            onClick={(e) => {
              e.stopPropagation();
              if (fileInputRef.current) {
                fileInputRef.current.value = null;
                setError("");
                setResultPDF(null);
              }
            }}
            title={t("Reset")}
          >
            <i className="fas fa-times"></i>
          </button>
        )}
      </div>

      {/* Status Messages */}
      {renderStatus()}

      {/* Error Message */}
      {error && (
        <div className="mt-3 p-3 rounded-3 border-start border-danger border-3" 
             style={{ backgroundColor: 'rgba(220, 53, 69, 0.1)' }}>
          <div className="d-flex align-items-center">
            <div 
              className="rounded-circle d-flex align-items-center justify-content-center me-3"
              style={{ 
                width: '40px', 
                height: '40px', 
                backgroundColor: 'rgba(220, 53, 69, 0.2)' 
              }}
            >
              <i className="fas fa-exclamation-triangle text-danger"></i>
            </div>
            <div className="flex-grow-1">
              <p className="text-danger fw-semibold mb-0">{error}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

UploadBiodata.propTypes = {
  extractedBiodata: PropTypes.func
};

export default UploadBiodata;
